import { closestCenter, DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS as DndCSS } from "@dnd-kit/utilities";
import {
  ArrowDown,
  ArrowUp,
  Bell,
  Box,
  ChevronRight,
  FolderPlus,
  HelpCircle,
  Home,
  MessageSquarePlus,
  MoreHorizontal,
  PanelLeft,
  Plus,
  Search,
  Settings,
  Shield,
  Star,
  Wrench,
} from "lucide-react";
import { type ReactElement, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../../components/Button/Button";
import { IconButton } from "../../components/IconButton/IconButton";
import type { ContextMenuItem } from "../../components/Menu/ContextMenu";
import { SpaceIcon } from "../../components/SpaceIcon/SpaceIcon";
import { Tooltip } from "../../components/Tooltip/Tooltip";
import { UsageIndicator, type UsageIndicatorProps } from "../UsageIndicator/UsageIndicator";
import { EmptyNote } from "./EmptyNote";
import { releasePointerFocus } from "./folderMoves";
import {
  DEFAULT_SIDEBAR_FOLDER_LABELS,
  fill,
  type SidebarFolderHandlers,
  type SidebarFolderLabels,
  type SidebarNavSpaceFolder,
} from "./folderTypes";
import { NavRow } from "./NavRow";
import { SidebarAccountMenu, type SidebarAccountMenuItem } from "./SidebarAccountMenu";
import { SidebarAllSpacesFlyout } from "./SidebarAllSpacesFlyout";
import { SidebarSpaceFolders } from "./SidebarSpaceFolders";
import { SidebarSpaceRow } from "./SidebarSpaceRow";

export interface SidebarNavSpaceItem {
  key: string;
  name: string;

  icon: ReactElement<{ size?: number }>;

  hue?: number;
  chatCount: number;
  pinned: boolean;

  private?: boolean;
}

/** Every built-in string, so apps can translate them. */
export interface SidebarNavLabels {
  expand: string;
  expandNavigation: string;
  collapse: string;
  collapseNavigation: string;
  search: string;
  newChat: string;
  home: string;
  pinnedSpaces: string;
  newSpace: string;
  nothingPinned: string;
  unpin: string;
  allSpaces: string;
  showSpaces: string;
  hideSpaces: string;
  emptyFolder: string;
  star: string;
  build: string;
  admin: string;
  notifications: string;
  /** The account menu's entry for `onOpenProfile`. */
  settings: string;
  help: string;
  feedback: string;
  account: string;
}

export const DEFAULT_SIDEBAR_NAV_LABELS: SidebarNavLabels = {
  expand: "Expand",
  expandNavigation: "Expand navigation",
  collapse: "Collapse",
  collapseNavigation: "Collapse navigation",
  search: "Search",
  newChat: "New chat",
  home: "Home",
  pinnedSpaces: "Favorite spaces",
  newSpace: "New space",
  nothingPinned: "No favorites yet. Star a space to pin it here.",
  unpin: "Remove from favorites",
  allSpaces: "All spaces",
  showSpaces: "Show spaces",
  hideSpaces: "Hide spaces",
  emptyFolder: "No spaces",
  star: "Add to favorites",
  build: "Build",
  admin: "Admin",
  notifications: "Notifications",
  settings: "Settings",
  help: "Help",
  feedback: "Send feedback",
  account: "Account",
};

export interface SidebarNavProps {
  logo: ReactNode;
  collapsed?: boolean;
  onToggleCollapse: () => void;
  onSearch: () => void;
  onNewChat: () => void;
  homeActive?: boolean;
  onOpenHome: () => void;

  spaces: SidebarNavSpaceItem[];

  renderSpacePopup?: (space: SidebarNavSpaceItem, trigger: ReactElement) => ReactNode;

  activeSpaceKey?: string;
  onOpenSpace: (key: string) => void;
  onTogglePinSpace: (key: string) => void;
  onNewSpace: () => void;
  /** Reorder favorites by dragging a row. Omit to leave favorites in their given order, undraggable. */
  onReorderFavorites?: (spaceKeys: string[]) => void;
  /** Controlled; omit for uncontrolled (starts expanded). */
  favoritesCollapsed?: boolean;
  onToggleFavoritesCollapsed?: (collapsed: boolean) => void;
  /**
   * Opens a full page of every space. Omit it when there is no such page: the "View all spaces" link
   * is hidden, and without `spaceFolders` there is no "All spaces" entry at all.
   */
  onOpenAllSpaces?: () => void;
  allSpacesActive?: boolean;
  /**
   * The user's own folders (Slack-style: menus, drag and drop, per-folder sort), rendered
   * top-level alongside Favorites rather than nested under "All spaces".
   */
  spaceFolders?: {
    folders: SidebarNavSpaceFolder[];
    handlers: SidebarFolderHandlers;
    labels?: Partial<SidebarFolderLabels>;
  };
  /**
   * How many spaces that are not in a folder show before "View all". Folders always show every space.
   * Pass `spaces` most recent first, since the first ones are the ones that show. Default 8.
   */
  looseSpacesLimit?: number;
  buildActive?: boolean;
  /** Omit to hide the Build entry (e.g. where Build isn't available). */
  onOpenBuild?: () => void;
  adminActive?: boolean;
  /** Omit to hide the Admin entry entirely (e.g. for users without admin access). */
  onOpenAdmin?: () => void;
  hasUnreadNotifications?: boolean;
  /** Called with the bell button, so the app can anchor its notifications panel to it. */
  onOpenNotifications: (anchor: HTMLElement) => void;
  /** True while the app's notifications panel is open: sets the bell's `aria-expanded` and pressed look. */
  notificationsOpen?: boolean;
  /** The account menu's Help entry. */
  onOpenHelp: () => void;
  /** Opens the app's feedback dialog, from the account menu. Omit to hide the Send feedback entry. */
  onOpenFeedback?: () => void;
  /** Omit to hide the usage indicator entirely (e.g. for a plan with no budget to show). */
  usage?: UsageIndicatorProps;
  userInitials: string;
  /** The account menu's Settings entry. The avatar opens that menu: Settings, Help, then Send feedback. */
  onOpenProfile: () => void;

  /** Overrides for the built-in strings (see DEFAULT_SIDEBAR_NAV_LABELS). */
  labels?: Partial<SidebarNavLabels>;
}

type ContentProps = Omit<SidebarNavProps, "logo" | "collapsed" | "onToggleCollapse" | "labels"> & {
  labels: SidebarNavLabels;
};

export function SidebarNav({ logo, collapsed = false, onToggleCollapse, labels, ...rest }: Readonly<SidebarNavProps>) {
  const l = { ...DEFAULT_SIDEBAR_NAV_LABELS, ...labels };
  return (
    <nav
      className={[
        "flex shrink-0 flex-col overflow-hidden border-r border-border-subtle bg-surface-sidebar py-3 transition-[width,padding] duration-200 ease-out",
        collapsed ? "w-14" : "w-60 px-2",
      ].join(" ")}
    >
      {}
      <div className="flex items-center justify-center pb-2">
        {collapsed ? (
          <Tooltip content={l.expand} placement="right">
            <IconButton
              icon={<PanelLeft />}
              aria-label={l.expandNavigation}
              variant="ghost"
              size="md"
              onClick={onToggleCollapse}
            />
          </Tooltip>
        ) : (
          <>
            <span className="flex h-6 shrink-0 items-center">{logo}</span>
            <span className="ml-auto shrink-0">
              <Tooltip content={l.collapse}>
                <IconButton
                  icon={<PanelLeft />}
                  aria-label={l.collapseNavigation}
                  variant="ghost"
                  size="md"
                  onClick={onToggleCollapse}
                />
              </Tooltip>
            </span>
          </>
        )}
      </div>
      {collapsed ? <CollapsedContent {...rest} labels={l} /> : <ExpandedContent {...rest} labels={l} />}
    </nav>
  );
}

/** The account menu's entries, in order: Settings, Help, then Send feedback when the app has it. */
function accountMenuItems({
  onOpenProfile,
  onOpenHelp,
  onOpenFeedback,
  labels: l,
}: Pick<ContentProps, "onOpenProfile" | "onOpenHelp" | "onOpenFeedback" | "labels">): SidebarAccountMenuItem[] {
  const items: SidebarAccountMenuItem[] = [
    { key: "settings", label: l.settings, icon: <Settings size={16} />, onSelect: onOpenProfile },
    { key: "help", label: l.help, icon: <HelpCircle size={16} />, onSelect: onOpenHelp },
  ];
  if (onOpenFeedback) {
    items.push({
      key: "feedback",
      label: l.feedback,
      icon: <MessageSquarePlus size={16} />,
      onSelect: onOpenFeedback,
      opensDialog: true,
    });
  }
  return items;
}

function RailIcon({
  icon,
  label,
  active,
  size = "lg",
  onClick,
}: Readonly<{
  icon: ReactElement<{ size?: number }>;
  label: string;
  active?: boolean;
  size?: "md" | "lg";
  onClick: () => void;
}>) {
  return (
    <Tooltip content={label} placement="right">
      <IconButton icon={icon} aria-label={label} variant="ghost" size={size} aria-pressed={active} onClick={onClick} />
    </Tooltip>
  );
}

/** The user's folders, the labels to show, and the spaces not in any folder. */
function folderView(spaces: SidebarNavSpaceItem[], spaceFolders: SidebarNavProps["spaceFolders"]) {
  const labels = { ...DEFAULT_SIDEBAR_FOLDER_LABELS, ...spaceFolders?.labels };
  const folders = spaceFolders?.folders.filter((f) => f.kind === "custom") ?? [];
  const foldered = new Set(folders.flatMap((f) => f.spaceKeys));
  const looseSpaces = spaceFolders ? spaces.filter((sp) => !sp.private && !foldered.has(sp.key)) : [];
  return { labels, folders, looseSpaces };
}

/** Runs the rail popup's anchor to the rail's edge, so the popup opens beside the rail, not over it. */
const RAIL_POPUP_ANCHOR = "[&>span]:-mr-3 [&>span]:pr-3";

function CollapsedContent({
  onSearch,
  onNewChat,
  homeActive,
  onOpenHome,
  spaces,
  activeSpaceKey,
  onOpenSpace,
  onNewSpace,
  onTogglePinSpace,
  renderSpacePopup,
  onOpenAllSpaces,
  allSpacesActive,
  spaceFolders,
  buildActive,
  onOpenBuild,
  adminActive,
  onOpenAdmin,
  hasUnreadNotifications,
  onOpenNotifications,
  notificationsOpen,
  onOpenHelp,
  onOpenFeedback,
  usage,
  userInitials,
  onOpenProfile,
  labels: l,
}: Readonly<ContentProps>) {
  const [allSpacesOpen, setAllSpacesOpen] = useState(false);
  const allSpacesRef = useRef<HTMLSpanElement>(null);
  const view = folderView(spaces, spaceFolders);
  const railSpace = (sp: SidebarNavSpaceItem) => {
    const trigger = (
      <button
        type="button"
        aria-label={sp.name}
        aria-pressed={sp.key === activeSpaceKey}
        onClick={() => onOpenSpace(sp.key)}
        className="grid size-9 cursor-pointer place-items-center rounded-lg transition-colors hover:bg-surface-base aria-pressed:bg-interactive-hover"
      >
        <SpaceIcon icon={sp.icon} hue={sp.hue as number} size="sm" />
      </button>
    );
    return (
      <span key={sp.key} className={RAIL_POPUP_ANCHOR} onMouseEnter={() => setAllSpacesOpen(false)}>
        {renderSpacePopup ? renderSpacePopup(sp, trigger) : trigger}
      </span>
    );
  };
  const privateSpace = spaces.find((sp) => sp.private);

  const pinned = spaces.filter((sp) => sp.pinned && !sp.private);

  return (
    <div className="flex min-h-0 flex-1 flex-col items-center gap-2">
      <RailIcon icon={<Search />} label={l.search} size="md" onClick={onSearch} />
      <Tooltip content={l.newChat} placement="right">
        <Button
          variant="secondaryBordered"
          size="sm"
          aria-label={l.newChat}
          // Button has no icon-only mode: make it the square its height already is.
          style={{ width: 32, padding: 0 }}
          onClick={onNewChat}
        >
          <Plus size={16} />
        </Button>
      </Tooltip>
      <div className="mt-1 flex min-h-0 flex-1 flex-col items-center gap-2">
        <RailIcon icon={<Home />} label={l.home} active={homeActive} onClick={onOpenHome} />
        {privateSpace &&
          (() => {
            const trigger = (
              <IconButton
                icon={privateSpace.icon}
                aria-label={privateSpace.name}
                variant="ghost"
                size="lg"
                aria-pressed={privateSpace.key === activeSpaceKey}
                onClick={() => onOpenSpace(privateSpace.key)}
              />
            );
            return renderSpacePopup ? (
              <span className={`contents ${RAIL_POPUP_ANCHOR}`} onMouseEnter={() => setAllSpacesOpen(false)}>
                {renderSpacePopup(privateSpace, trigger)}
              </span>
            ) : (
              <Tooltip content={privateSpace.name} placement="right">
                {trigger}
              </Tooltip>
            );
          })()}
        {pinned.map(railSpace)}
        {(spaceFolders || onOpenAllSpaces) && (
          <span ref={allSpacesRef} className="-mr-2 inline-flex pr-2">
            <RailIcon
              icon={<Box />}
              label={l.allSpaces}
              active={allSpacesActive || allSpacesOpen}
              onClick={() => (spaceFolders ? setAllSpacesOpen((o) => !o) : onOpenAllSpaces?.())}
            />
          </span>
        )}
        {spaceFolders && (
          <SidebarAllSpacesFlyout
            isOpen={allSpacesOpen}
            anchorRef={allSpacesRef}
            onClose={() => setAllSpacesOpen(false)}
            title={l.allSpaces}
            browse="folders"
            onViewPage={onOpenAllSpaces}
            spaces={spaces.filter((sp) => !sp.private)}
            looseSpaces={view.looseSpaces}
            folders={view.folders}
            handlers={spaceFolders.handlers}
            labels={view.labels}
            activeSpaceKey={activeSpaceKey}
            onOpenSpace={onOpenSpace}
            onTogglePinSpace={onTogglePinSpace}
            starLabel={l.star}
            unpinLabel={l.unpin}
            onNewSpace={onNewSpace}
            newSpaceLabel={l.newSpace}
          />
        )}
      </div>
      <div className="flex flex-col items-center gap-2 pt-1">
        {onOpenBuild && <RailIcon icon={<Wrench />} label={l.build} active={buildActive} onClick={onOpenBuild} />}
        {onOpenAdmin && <RailIcon icon={<Shield />} label={l.admin} active={adminActive} onClick={onOpenAdmin} />}
      </div>
      <div className="flex w-full flex-col items-center gap-2 border-t border-border-subtle pt-2">
        {usage && <UsageIndicator {...usage} />}
        <Tooltip content={l.notifications} placement="right">
          <IconButton
            icon={<Bell />}
            aria-label={l.notifications}
            variant="ghost"
            size="md"
            showDot={hasUnreadNotifications}
            aria-haspopup="dialog"
            aria-expanded={notificationsOpen ?? false}
            onClick={(event) => onOpenNotifications(event.currentTarget)}
          />
        </Tooltip>
        <SidebarAccountMenu
          userInitials={userInitials}
          label={l.account}
          items={accountMenuItems({ onOpenProfile, onOpenHelp, onOpenFeedback, labels: l })}
          placement="right"
          tooltipPlacement="right"
          anchorClassName="flex justify-center self-stretch"
          triggerClassName="mt-1"
        />
      </div>
    </div>
  );
}

function SortableFavorite({
  space,
  activeSpaceKey,
  onOpenSpace,
  onTogglePinSpace,
  unpinLabel,
  draggable,
  menu,
}: Readonly<{
  space: SidebarNavSpaceItem;
  activeSpaceKey?: string;
  onOpenSpace: (key: string) => void;
  onTogglePinSpace: (key: string) => void;
  unpinLabel: string;
  draggable: boolean;
  menu: { label: string; ariaLabel: string; items: ContextMenuItem[] };
}>) {
  const { listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `favorite:${space.key}`,
    disabled: !draggable,
  });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: DndCSS.Translate.toString(transform), transition }}
      className={isDragging ? "relative z-20 opacity-60" : undefined}
    >
      <SidebarSpaceRow
        space={space}
        active={space.key === activeSpaceKey}
        starLabel={unpinLabel}
        onOpen={() => onOpenSpace(space.key)}
        onToggleStar={() => onTogglePinSpace(space.key)}
        menu={menu}
        // Pointer drag only: the row holds buttons, so dnd-kit's role and tabindex on it would nest controls.
        // Move up / Move down in the menu are the keyboard way to reorder.
        dragRowProps={
          draggable && listeners
            ? { onPointerDown: listeners.onPointerDown as React.PointerEventHandler<HTMLDivElement> }
            : undefined
        }
      />
    </div>
  );
}

function ExpandedContent({
  onSearch,
  onNewChat,
  homeActive,
  onOpenHome,
  spaces,
  activeSpaceKey,
  onOpenSpace,
  onTogglePinSpace,
  onNewSpace,
  onReorderFavorites,
  favoritesCollapsed,
  onToggleFavoritesCollapsed,
  onOpenAllSpaces,
  allSpacesActive,
  spaceFolders,
  looseSpacesLimit = 8,
  buildActive,
  onOpenBuild,
  adminActive,
  onOpenAdmin,
  hasUnreadNotifications,
  onOpenNotifications,
  notificationsOpen,
  onOpenHelp,
  onOpenFeedback,
  usage,
  userInitials,
  onOpenProfile,
  labels: l,
}: Readonly<ContentProps>) {
  const privateSpace = spaces.find((sp) => sp.private);

  const pinned = spaces.filter((sp) => sp.pinned && !sp.private);

  const [favoritesCollapsedState, setFavoritesCollapsedState] = useState(false);
  const favCollapsed = favoritesCollapsed ?? favoritesCollapsedState;
  const toggleFavorites = () => {
    const next = !favCollapsed;
    onToggleFavoritesCollapsed?.(next);
    if (favoritesCollapsed === undefined) setFavoritesCollapsedState(next);
  };

  const favSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const onFavoriteDragEnd = ({ active, over, activatorEvent }: DragEndEvent) => {
    releasePointerFocus(activatorEvent);
    if (!onReorderFavorites || !over || active.id === over.id) return;
    const ids = pinned.map((sp) => `favorite:${sp.key}`);
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    const next = pinned.map((sp) => sp.key);
    next.splice(to, 0, ...next.splice(from, 1));
    onReorderFavorites(next);
  };

  const folderLabels = spaceFolders && { ...DEFAULT_SIDEBAR_FOLDER_LABELS, ...spaceFolders.labels };
  const menuLabels = { ...DEFAULT_SIDEBAR_FOLDER_LABELS, ...spaceFolders?.labels };
  const favoriteMenu = (sp: SidebarNavSpaceItem, index: number) => {
    const move = (step: -1 | 1) => {
      const next = pinned.map((p) => p.key);
      next.splice(index + step, 0, ...next.splice(index, 1));
      onReorderFavorites?.(next);
    };
    const items: ContextMenuItem[] = [
      ...(spaceFolders?.handlers.onOpenSpaceSettings
        ? [
            {
              label: menuLabels.spaceSettings,
              icon: <Settings size={14} />,
              onSelect: () => spaceFolders.handlers.onOpenSpaceSettings?.(sp.key),
            },
          ]
        : []),
      ...(onReorderFavorites && index > 0
        ? [
            {
              label: menuLabels.moveUp,
              icon: <ArrowUp size={14} />,
              group: menuLabels.moveGroup,
              onSelect: () => move(-1),
            },
          ]
        : []),
      ...(onReorderFavorites && index < pinned.length - 1
        ? [
            {
              label: menuLabels.moveDown,
              icon: <ArrowDown size={14} />,
              group: menuLabels.moveGroup,
              onSelect: () => move(1),
            },
          ]
        : []),
    ];
    return { label: menuLabels.spaceMenu, ariaLabel: fill(menuLabels.spaceMenuAria, sp.name), items };
  };
  const customFolders = spaceFolders?.folders.filter((s) => s.kind === "custom") ?? [];
  const folderedKeys = new Set(customFolders.flatMap((s) => s.spaceKeys));
  const looseSpaces = spaceFolders ? spaces.filter((sp) => !sp.private && !folderedKeys.has(sp.key)) : [];

  // The active space always shows, so the user never loses their place past the cap.
  const visibleLoose = looseSpaces.slice(0, looseSpacesLimit);
  const activeLoose = looseSpaces.find((sp) => sp.key === activeSpaceKey);
  if (activeLoose && !visibleLoose.includes(activeLoose)) visibleLoose.push(activeLoose);
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const viewAllRef = useRef<HTMLSpanElement>(null);

  const [allSpacesExpanded, setAllSpacesExpanded] = useState(true);
  const [newFolderRequest, setNewFolderRequest] = useState(0);

  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const [scrolledTop, setScrolledTop] = useState(false);
  const [scrolledBottom, setScrolledBottom] = useState(false);
  const updateScrollDividers = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setScrolledTop(el.scrollTop > 0);
    setScrolledBottom(Math.ceil(el.scrollTop + el.clientHeight) < el.scrollHeight);
  }, []);
  // Content height changes with the item count (folders expanding, spaces added, "View all"...),
  // not just the scroll container's own size, which a plain resize listener wouldn't catch.
  useEffect(() => {
    const content = scrollContentRef.current;
    if (!content) return;
    updateScrollDividers();
    const observer = new ResizeObserver(updateScrollDividers);
    observer.observe(content);
    return () => observer.disconnect();
  }, [updateScrollDividers]);
  const startNewFolder = () => {
    setAllSpacesExpanded(true);
    setNewFolderRequest((n) => n + 1);
  };

  return (
    <>
      {}
      <button
        type="button"
        onClick={onSearch}
        className="mb-2 flex cursor-pointer items-center gap-2 rounded-lg border border-border-input bg-surface-base px-3 py-2 text-content-tertiary transition-colors hover:border-border-focus btn-focus-ring"
      >
        <Search size={15} />
        <span className="text-xs">{l.search}</span>
        {}
        <span className="ml-auto rounded border border-border-default bg-surface-elevated px-1 py-0 font-mono text-[10px] leading-[18px]">
          ⌘K
        </span>
      </button>
      {}
      <Button variant="secondaryBordered" size="sm" leadingIcon={<Plus size={16} />} fullWidth onClick={onNewChat}>
        {l.newChat}
      </Button>
      {}
      <div
        ref={scrollRef}
        className={[
          "-mx-1 mt-1 flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto border-t px-1 py-1",
          scrolledTop ? "border-border-subtle" : "border-transparent",
        ].join(" ")}
        onScroll={updateScrollDividers}
      >
        <div ref={scrollContentRef}>
          <div className="space-y-1">
            <NavRow icon={<Home />} active={homeActive} onClick={onOpenHome}>
              {l.home}
            </NavRow>
            {privateSpace && (
              <NavRow
                icon={privateSpace.icon}
                active={privateSpace.key === activeSpaceKey}
                onClick={() => onOpenSpace(privateSpace.key)}
              >
                {privateSpace.name}
              </NavRow>
            )}
          </div>
          <div className="flex items-center justify-between py-1 pt-3 pl-3 pr-3">
            <button
              type="button"
              aria-expanded={!favCollapsed}
              onClick={toggleFavorites}
              className="flex min-w-0 flex-1 cursor-pointer items-center gap-1 rounded-md text-left transition-colors hover:text-content-primary btn-focus-ring"
            >
              <span className="min-w-0 truncate [font:var(--type-eyebrow)] uppercase tracking-wide text-content-tertiary">
                {l.pinnedSpaces}
              </span>
              <ChevronRight
                size={13}
                className={["shrink-0 transition-transform", favCollapsed ? "" : "rotate-90"].join(" ")}
              />
            </button>
          </div>
          {!favCollapsed && (
            <div className="space-y-1">
              {pinned.length === 0 && <EmptyNote icon={<Star />}>{l.nothingPinned}</EmptyNote>}
              <DndContext
                sensors={favSensors}
                collisionDetection={closestCenter}
                onDragEnd={onFavoriteDragEnd}
                onDragCancel={({ activatorEvent }) => releasePointerFocus(activatorEvent)}
              >
                <SortableContext
                  items={pinned.map((sp) => `favorite:${sp.key}`)}
                  strategy={verticalListSortingStrategy}
                >
                  {pinned.map((sp, index) => (
                    <SortableFavorite
                      key={sp.key}
                      space={sp}
                      activeSpaceKey={activeSpaceKey}
                      onOpenSpace={onOpenSpace}
                      onTogglePinSpace={onTogglePinSpace}
                      unpinLabel={l.unpin}
                      draggable={!!onReorderFavorites}
                      menu={favoriteMenu(sp, index)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          )}
          <div className="mt-1 space-y-1">
            {spaceFolders && folderLabels ? (
              <div>
                <div className="flex items-center justify-between py-1 pt-3 pl-3 pr-2">
                  <button
                    type="button"
                    aria-expanded={allSpacesExpanded}
                    onClick={() => setAllSpacesExpanded((e) => !e)}
                    className="flex min-w-0 flex-1 cursor-pointer items-center gap-1 rounded-md text-left transition-colors hover:text-content-primary btn-focus-ring"
                  >
                    <span className="min-w-0 truncate [font:var(--type-eyebrow)] uppercase tracking-wide text-content-tertiary">
                      {l.allSpaces}
                    </span>
                    <ChevronRight
                      size={13}
                      className={["shrink-0 transition-transform", allSpacesExpanded ? "rotate-90" : ""].join(" ")}
                    />
                  </button>
                  <div className="flex items-center gap-1">
                    <Tooltip content={folderLabels.newFolder}>
                      <IconButton
                        icon={<FolderPlus />}
                        aria-label={folderLabels.newFolder}
                        variant="ghost"
                        size="sm"
                        onClick={startNewFolder}
                      />
                    </Tooltip>
                    <Tooltip content={l.newSpace}>
                      <IconButton
                        icon={<Plus />}
                        aria-label={l.newSpace}
                        variant="ghost"
                        size="sm"
                        onClick={onNewSpace}
                      />
                    </Tooltip>
                  </div>
                </div>
                {allSpacesExpanded && (
                  <div className="mt-1 space-y-1">
                    <SidebarSpaceFolders
                      spaces={spaces.filter((sp) => !sp.private)}
                      looseSpaces={visibleLoose}
                      folders={customFolders}
                      handlers={spaceFolders.handlers}
                      labels={folderLabels}
                      activeSpaceKey={activeSpaceKey}
                      onOpenSpace={onOpenSpace}
                      onTogglePinSpace={onTogglePinSpace}
                      requestNewFolder={newFolderRequest}
                      starLabel={l.star}
                      unpinLabel={l.unpin}
                      onNewSpace={onNewSpace}
                      newSpaceLabel={l.newSpace}
                    />
                    {looseSpaces.length > looseSpacesLimit && (
                      <>
                        <span ref={viewAllRef} className="-mr-2 block pr-2">
                          <NavRow
                            icon={<MoreHorizontal />}
                            expanded={viewAllOpen}
                            onClick={() => setViewAllOpen((o) => !o)}
                          >
                            {fill(folderLabels.viewAll, "", "", String(looseSpaces.length))}
                          </NavRow>
                        </span>
                        <SidebarAllSpacesFlyout
                          isOpen={viewAllOpen}
                          anchorRef={viewAllRef}
                          onClose={() => setViewAllOpen(false)}
                          title={l.allSpaces}
                          browse="list"
                          spaces={spaces.filter((sp) => !sp.private)}
                          looseSpaces={looseSpaces}
                          folders={customFolders}
                          handlers={spaceFolders.handlers}
                          labels={folderLabels}
                          activeSpaceKey={activeSpaceKey}
                          onOpenSpace={onOpenSpace}
                          onTogglePinSpace={onTogglePinSpace}
                          onViewPage={onOpenAllSpaces}
                          starLabel={l.star}
                          unpinLabel={l.unpin}
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
            ) : (
              onOpenAllSpaces && (
                <NavRow icon={<Box />} active={allSpacesActive} onClick={onOpenAllSpaces}>
                  {l.allSpaces}
                </NavRow>
              )
            )}
          </div>
        </div>
      </div>
      {(onOpenBuild || onOpenAdmin) && (
        <div
          className={["space-y-1 border-t pt-1", scrolledBottom ? "border-border-subtle" : "border-transparent"].join(
            " ",
          )}
        >
          {onOpenBuild && (
            <NavRow icon={<Wrench />} active={buildActive} onClick={onOpenBuild}>
              {l.build}
            </NavRow>
          )}
          {onOpenAdmin && (
            <NavRow icon={<Shield />} active={adminActive} onClick={onOpenAdmin}>
              {l.admin}
            </NavRow>
          )}
        </div>
      )}
      {}
      <div className="-mx-2 mt-1 flex items-center gap-2 border-t border-border-subtle px-2 pt-2">
        {usage && <UsageIndicator {...usage} />}
        <Tooltip content={l.notifications}>
          <IconButton
            icon={<Bell />}
            aria-label={l.notifications}
            variant="ghost"
            size="md"
            showDot={hasUnreadNotifications}
            aria-haspopup="dialog"
            aria-expanded={notificationsOpen ?? false}
            onClick={(event) => onOpenNotifications(event.currentTarget)}
          />
        </Tooltip>
        <span className="ml-auto">
          <SidebarAccountMenu
            userInitials={userInitials}
            label={l.account}
            items={accountMenuItems({ onOpenProfile, onOpenHelp, onOpenFeedback, labels: l })}
            placement="top"
          />
        </span>
      </div>
    </>
  );
}
